import React from 'react';
import { ImageDropzone } from './ImageDropzone';

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #cbd5e1',
    marginTop: 4,
    boxSizing: 'border-box',
    fontSize: 14,
    fontFamily: "'Inter', Arial, sans-serif",
    background: 'white',
};

const labelStyle = { fontSize: 12, fontWeight: 700, color: '#475569' };

const sectionTitleStyle = (color = '#1e40af') => ({
    fontSize: 12,
    fontWeight: 800,
    color,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 14,
    marginTop: 10,
    borderBottom: `2px solid ${color === '#1e40af' ? '#e0e7ff' : '#d1fae5'}`,
    paddingBottom: 6,
});

/**
 * EmpresaFormFields — campos comunes del formulario de empresa.
 * Props:
 *  - form          : objeto con todos los valores del formulario
 *  - onChange      : handler para inputs/selects (recibe el evento nativo)
 *  - onImageChange : ({ imagenLogo?, imagenIcono? }) => void
 *  - isSuperAdmin  : boolean — habilita maxSedes e indActivo
 *  - readonlyCodigo: boolean — muestra codigoEntidad como readonly
 *  - accentColor   : string CSS — color de los títulos de sección
 */
export const EmpresaFormFields = ({
    form,
    onChange,
    onImageChange,
    isSuperAdmin = true,
    readonlyCodigo = false,
    accentColor = '#1e40af',
}) => {
    return (
        <>
            {/* Sección 1: Datos básicos */}
            <div style={sectionTitleStyle(accentColor)}>
                Información de la Empresa
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>
                        Código Entidad{!readonlyCodigo && ' *'}
                    </label>
                    <input
                        id="codigoEntidad"
                        type="text"
                        value={form.codigoEntidad}
                        onChange={readonlyCodigo ? undefined : onChange}
                        readOnly={readonlyCodigo}
                        placeholder="Ej: clinica-salud"
                        style={{
                            ...inputStyle,
                            background: readonlyCodigo ? '#f1f5f9' : 'white',
                            color: readonlyCodigo ? '#64748b' : 'inherit',
                            cursor: readonlyCodigo ? 'not-allowed' : 'text',
                        }}
                    />
                </div>
                <div>
                    <label style={labelStyle}>Nombre Empresa *</label>
                    <input
                        id="nombreEntidad"
                        type="text"
                        value={form.nombreEntidad}
                        onChange={onChange}
                        required
                        placeholder="Ej: Clínica Salud Total"
                        style={inputStyle}
                    />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>NIT / Identificación</label>
                    <input id="nit" type="text" value={form.nit} onChange={onChange} placeholder="Ej: 900123456-7" style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Dirección</label>
                    <input id="direccion" type="text" value={form.direccion} onChange={onChange} placeholder="Ej: Cra 15 # 80-20" style={inputStyle} />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>Teléfono</label>
                    <input id="telefonoContacto" type="text" value={form.telefonoContacto} onChange={onChange} placeholder="Ej: 6013004000" style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Email Contacto</label>
                    <input id="emailContacto" type="email" value={form.emailContacto} onChange={onChange} placeholder="contacto@empresa.com" style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Ciudad</label>
                    <input id="ciudad" type="text" value={form.ciudad} onChange={onChange} placeholder="Ej: Bogotá" style={inputStyle} />
                </div>
            </div>

            {/* Sección 2: Imágenes */}
            <div style={sectionTitleStyle(accentColor)}>Branding / Imágenes</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <ImageDropzone
                    label="Logo de la Empresa (Header / Pantallas)"
                    value={form.imagenLogo}
                    onChange={(url) => onImageChange({ imagenLogo: url })}
                    recommendedSpecs="Logo horizontal: 300x80 px | PNG transparente o SVG (máx. 2MB)"
                />
                <ImageDropzone
                    label="Ícono / Isotipo de la Empresa"
                    value={form.imagenIcono}
                    onChange={(url) => onImageChange({ imagenIcono: url })}
                    recommendedSpecs="Ícono cuadrado: 128x128 px | PNG o SVG (máx. 2MB)"
                />
            </div>

            {/* Sección 3: Configuración */}
            <div style={sectionTitleStyle(accentColor)}>Configuración del Sistema</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>Tipo de Anuncio de Llamado</label>
                    <select id="llamadoVoz" value={form.llamadoVoz} onChange={onChange} style={inputStyle}>
                        <option value={0}>🔔 Sonido / Timbre (Gong tradicional)</option>
                        <option value={1}>🗣️ Llamado por Voz Sintetizada</option>
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>Acento / Idioma de Voz</label>
                    <select
                        id="voz"
                        value={form.voz}
                        onChange={onChange}
                        disabled={form.llamadoVoz === 0}
                        style={{ ...inputStyle, background: form.llamadoVoz === 0 ? '#f1f5f9' : 'white', cursor: form.llamadoVoz === 0 ? 'not-allowed' : 'auto' }}
                    >
                        <option value="es-CO">🇨🇴 Español (Colombia)</option>
                        <option value="es-MX">🇲🇽 Español (México)</option>
                        <option value="es-AR">🇦🇷 Español (Argentina)</option>
                        <option value="es-CL">🇨🇱 Español (Chile)</option>
                        <option value="es-ES">🇪🇸 Español (España)</option>
                        <option value="es-US">🇺🇸 Español (EE.UU.)</option>
                    </select>
                </div>
            </div>

            {/* Sección 4: Comercial */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>Sedes Máximas Contratadas</label>
                    <input
                        id="maxSedes"
                        type="number"
                        min="1"
                        value={form.maxSedes}
                        onChange={onChange}
                        disabled={!isSuperAdmin}
                        style={{ ...inputStyle, fontWeight: 700, color: '#0d7367', fontSize: 16, background: !isSuperAdmin ? '#f1f5f9' : 'white', cursor: !isSuperAdmin ? 'not-allowed' : 'auto' }}
                    />
                </div>
                <div>
                    <label style={labelStyle}>Estado</label>
                    <select
                        id="indActivo"
                        value={form.indActivo}
                        onChange={onChange}
                        disabled={!isSuperAdmin}
                        style={{ ...inputStyle, background: !isSuperAdmin ? '#f1f5f9' : 'white', cursor: !isSuperAdmin ? 'not-allowed' : 'auto' }}
                    >
                        <option value={1}>✅ Activa</option>
                        <option value={0}>❌ Inactiva</option>
                    </select>
                </div>
            </div>

            {!isSuperAdmin && (
                <div style={{
                    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
                    padding: '10px 14px', fontSize: 12, color: '#1e40af', marginBottom: 4,
                }}>
                    ℹ️ Los campos <b>Sedes Máximas</b> y <b>Estado</b> son administrados por el equipo de soporte técnico.
                </div>
            )}
        </>
    );
};
